import React, { useState, useCallback, useMemo } from 'react';
import { Table, Button, Empty, Tag, Card, Popconfirm } from 'antd';
import {
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  InboxOutlined,
  WarningOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { WorkOrderLineItem } from '../../types';
import { LineItemRow } from './LineItemRow';
import { LineItemDrawer } from './LineItemDrawer';
import { AddLineItemFAB } from './AddLineItemFAB';
import { formatCurrency } from '../../utils/formatCurrency';

export interface LineItemListProps {
  items: WorkOrderLineItem[];
  isLocked: boolean;
  isMobile: boolean;
  onDeleteItem: (id: number) => Promise<void> | void;

  // ── External editor mode (work orders) ──
  // When onEditItem is provided, Add/Edit open a parent-owned full editor
  // (WorkOrderLineItemForm) that supports parts CRUD, labor + per-part
  // discounts, and accurate price breakdowns. The built-in drawer is not used.
  onAddItem?: () => void;
  onEditItem?: (item: WorkOrderLineItem) => void;

  // ── Internal editor mode (estimates) ──
  // When onEditItem is NOT provided, the built-in LineItemDrawer handles edits
  // and saves via onSaveItem. workOrderId is passed through to that drawer.
  onSaveItem?: (values: Partial<WorkOrderLineItem>) => Promise<void>;
  workOrderId?: number;
}

const computeLineTotal = (item: WorkOrderLineItem): number => {
  if (item.line_total != null) return item.line_total;
  const laborTotal = ((item.labor_hours || 0) * (item.labor_rate || 0)) - (item.labor_discount_amount || 0);
  const partsTotal = item.parts?.reduce((sum, p) => sum + p.quantity * p.unit_price, 0) || 0;
  return laborTotal + partsTotal;
};

const shopStatusConfig: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
  new: { color: 'blue', label: 'New', icon: <InboxOutlined /> },
  in_progress: { color: 'processing', label: 'In Progress', icon: <ToolOutlined /> },
  waiting_parts: { color: 'orange', label: 'Waiting Parts', icon: <ClockCircleOutlined /> },
  need_approval: { color: 'warning', label: 'Needs Approval', icon: <WarningOutlined /> },
  completed: { color: 'success', label: 'Completed', icon: <CheckCircleOutlined /> },
};

const approvalColor: Record<string, string> = {
  pending: 'default',
  approved: 'success',
  declined: 'error',
};

/** Unified responsive line item list: cards on mobile, table on desktop. */
export const LineItemList: React.FC<LineItemListProps> = ({
  items,
  isLocked,
  isMobile,
  onDeleteItem,
  onAddItem,
  onEditItem,
  onSaveItem,
  workOrderId,
}) => {
  // Work orders pass onEditItem and use the full external editor; estimates
  // fall back to the built-in drawer.
  const useExternalEditor = typeof onEditItem === 'function';

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<WorkOrderLineItem | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.line_number - b.line_number),
    [items],
  );

  const grandTotal = useMemo(
    () => items.reduce((sum, item) => sum + computeLineTotal(item), 0),
    [items],
  );

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedItem(null);
    setIsNewItem(false);
  }, []);

  const handleAdd = useCallback(() => {
    if (useExternalEditor) {
      onAddItem?.();
    } else {
      setSelectedItem(null);
      setIsNewItem(true);
      setDrawerOpen(true);
    }
  }, [useExternalEditor, onAddItem]);

  const handleEdit = useCallback((item: WorkOrderLineItem) => {
    if (useExternalEditor) {
      onEditItem?.(item);
    } else {
      setSelectedItem(item);
      setIsNewItem(false);
      setDrawerOpen(true);
    }
  }, [useExternalEditor, onEditItem]);

  // Built-in drawer is only mounted in internal-editor mode.
  const internalDrawer = !useExternalEditor && (
    <LineItemDrawer
      open={drawerOpen}
      item={selectedItem}
      isNew={isNewItem}
      isLocked={isLocked}
      workOrderId={workOrderId ?? 0}
      onSave={onSaveItem ?? (async () => {})}
      onDelete={(id) => Promise.resolve(onDeleteItem(id))}
      onClose={closeDrawer}
    />
  );

  // ── Empty state ──
  if (items.length === 0) {
    return (
      <Card styles={{ body: { padding: isMobile ? '40px 16px' : '50px 24px' } }} style={{ overflow: 'hidden' }}>
        <Empty
          description={
            <>
              <div style={{ fontSize: isMobile ? 14 : 15, color: '#6b7280' }}>No line items yet</div>
              <div style={{ fontSize: isMobile ? 12 : 13, color: '#9ca3af' }}>
                {isMobile
                  ? 'Tap the + button to add parts, labor, or services.'
                  : 'Click Add Line Item to add parts, labor, or services.'}
              </div>
            </>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {!isMobile && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={isLocked}
              style={{ marginTop: 14 }}
            >
              Add First Line Item
            </Button>
          )}
        </Empty>
        {isMobile && <AddLineItemFAB onClick={handleAdd} disabled={isLocked} />}
        {internalDrawer}
      </Card>
    );
  }

  // ── Desktop table columns ──
  const columns: ColumnsType<WorkOrderLineItem> = [
    {
      title: '#',
      dataIndex: 'line_number',
      key: 'line_number',
      width: 50,
      render: (val: number) => <span style={{ color: '#9ca3af' }}>{val}</span>,
    },
    {
      title: 'Concern',
      dataIndex: 'concern',
      key: 'concern',
      ellipsis: true,
      render: (text: string, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text || 'Untitled'}</div>
          {record.correction && (
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{record.correction}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_: unknown, record) => {
        const cfg = shopStatusConfig[record.shop_status] || shopStatusConfig.new;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Tag color={cfg.color} icon={cfg.icon} style={{ margin: 0 }}>{cfg.label}</Tag>
            <Tag color={approvalColor[record.customer_approval_status] || 'default'} style={{ margin: 0, fontSize: 11 }}>
              {record.customer_approval_status}
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Labor',
      key: 'labor',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, record) => {
        const hours = record.labor_hours || 0;
        const rate = record.labor_rate || 0;
        if (hours === 0) return <span style={{ color: '#d1d5db' }}>--</span>;
        const discount = record.labor_discount_amount || 0;
        return (
          <div style={{ fontSize: 13 }}>
            <div>{hours}h @ ${rate}</div>
            {discount > 0 && (
              <div style={{ color: '#52c41a', fontSize: 11 }}>-{formatCurrency(discount)}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Parts',
      key: 'parts_count',
      width: 70,
      align: 'center' as const,
      render: (_: unknown, record) => {
        const count = record.parts?.length || 0;
        return count > 0 ? <span>{count}</span> : <span style={{ color: '#d1d5db' }}>--</span>;
      },
    },
    {
      title: 'Total',
      key: 'total',
      width: 100,
      align: 'right' as const,
      render: (_: unknown, record) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(computeLineTotal(record))}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_: unknown, record) => (
        <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <Button type="link" size="small" onClick={() => handleEdit(record)} disabled={isLocked}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this line item?"
            description="This cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => record.id != null && onDeleteItem(record.id)}
            disabled={isLocked}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} disabled={isLocked} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  // ── Mobile layout ──
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sortedItems.map((lineItem) => (
          <div key={lineItem.id} style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <LineItemRow
                item={lineItem}
                layout="card"
                isMobile={isMobile}
                isLocked={isLocked}
                onTap={() => handleEdit(lineItem)}
              />
            </div>
            {!isLocked && (
              <Popconfirm
                title="Delete this line item?"
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => lineItem.id != null && onDeleteItem(lineItem.id)}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  style={{ height: 'auto', alignSelf: 'center' }}
                  aria-label="Delete line item"
                />
              </Popconfirm>
            )}
          </div>
        ))}

        {/* Grand total */}
        <div style={{
          textAlign: 'right',
          marginTop: 24,
          fontSize: 20,
          fontWeight: 600,
          color: '#2563eb',
        }}>
          Total: {formatCurrency(grandTotal)}
        </div>

        <AddLineItemFAB onClick={handleAdd} disabled={isLocked} />

        {internalDrawer}
      </div>
    );
  }

  // ── Desktop layout ──
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={isLocked}
        >
          Add Line Item
        </Button>
      </div>

      <Table
        dataSource={sortedItems}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        onRow={(record) => ({
          onClick: () => handleEdit(record),
          style: { cursor: 'pointer' },
        })}
      />

      {/* Grand total */}
      <div style={{
        textAlign: 'right',
        marginTop: 24,
        fontSize: 20,
        fontWeight: 600,
        color: '#2563eb',
      }}>
        Total: {formatCurrency(grandTotal)}
      </div>

      {internalDrawer}
    </div>
  );
};
